import React from 'react';
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';
import { MapPin, Calendar, CheckCircle2, ArrowUpRight } from 'lucide-react';

const Projects = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const projects = [
    {
      title: 'Corredor Biológico Alto Paraná',
      location: 'Naranjal, Alto Paraná',
      year: '2022 - Presente',
      status: 'Activo',
      image: 'https://images.unsplash.com/photo-1511497584788-876760111969?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      description: 'Proyecto de reforestación de 12,000 hectáreas con especies nativas, creando un corredor biológico entre áreas protegidas.',
      credits: '45,000',
      species: '28',
    },
    {
      title: 'Reserva Forestal Itaipú',
      location: 'Hernandarias, Alto Paraná',
      year: '2023 - Presente',
      status: 'Activo',
      image: 'https://images.unsplash.com/photo-1502082553048-f009c37129b9?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      description: 'Conservación de 8,500 hectáreas de bosque nativo en colaboración con la Entidad Binacional Itaipú.',
      credits: '32,000',
      species: '45',
    },
    {
      title: 'Suelos Regenerativos del Este',
      location: 'Minga Guazú, Alto Paraná',
      year: '2024 - Presente',
      status: 'Activo',
      image: 'https://images.unsplash.com/photo-1625246333195-78d9c38ad449?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      description: 'Implementación de agricultura regenerativa en 3,200 hectáreas de pequeños productores locales.',
      credits: '18,000',
      species: '12',
    },
    {
      title: 'Manglares del Pantanal',
      location: 'Pilar, Ñeembucú',
      year: '2024 - Presente',
      status: 'En Desarrollo',
      image: 'https://images.unsplash.com/photo-1587595431973-160d0d94add1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      description: 'Restauración de ecosistemas de humedales para captura de carbono azul y protección de biodiversidad.',
      credits: '25,000',
      species: '35',
    },
  ];

  return (
    <section id="proyectos" style={{
      background: 'linear-gradient(180deg, var(--dark-900) 0%, var(--dark) 100%)',
      padding: 'var(--space-3xl) 0',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Background glow */}
      <div style={{
        position: 'absolute',
        bottom: '-30%',
        left: '-20%',
        width: '600px',
        height: '600px',
        background: 'radial-gradient(circle, rgba(16,185,129,0.04) 0%, transparent 70%)',
        borderRadius: '50%',
        pointerEvents: 'none',
      }} />

      <div className="container" ref={ref}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto 80px' }}
        >
          <span style={{
            display: 'inline-block',
            padding: '8px 20px',
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.2)',
            borderRadius: '50px',
            color: 'var(--primary-400)',
            fontSize: '0.85rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '2px',
            marginBottom: '20px',
          }}>
            Proyectos Activos
          </span>
          <h2 style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            marginBottom: '20px',
          }}>
            Impacto Real en{' '}
            <span className="text-gradient">Paraguay</span>
          </h2>
          <p style={{
            color: 'var(--text-secondary)',
            fontSize: '1.1rem',
            lineHeight: 1.7,
          }}>
            Cada proyecto genera créditos de carbono verificados mientras restaura 
            ecosistemas y beneficia comunidades locales.
          </p>
        </motion.div>

        {/* Projects Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '30px',
        }} className="projects-grid">
          {projects.map((project, i) => (
            <motion.div
              key={project.title}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: i * 0.15 }}
              style={{
                position: 'relative',
                borderRadius: '24px',
                overflow: 'hidden',
                cursor: 'pointer',
                border: '1px solid var(--border)',
                transition: 'all 0.4s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = 'var(--shadow-xl)';
                e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = 'var(--border)';
              }}
            >
              {/* Image */}
              <div style={{ position: 'relative', height: '280px', overflow: 'hidden' }}>
                <img
                  src={project.image}
                  alt={project.title}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'transform 0.6s ease',
                  }}
                  className="project-img"
                />
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(to top, var(--dark-900) 0%, transparent 60%)',
                }} />

                {/* Status badge */}
                <div style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 14px',
                  background: project.status === 'Activo' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(245, 158, 11, 0.2)',
                  border: `1px solid ${project.status === 'Activo' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`,
                  borderRadius: '50px',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: project.status === 'Activo' ? 'var(--primary-300)' : 'var(--accent-300)',
                }}>
                  <CheckCircle2 size={14} />
                  {project.status}
                </div>
              </div>

              {/* Content */}
              <div style={{ padding: '28px' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  marginBottom: '12px',
                  flexWrap: 'wrap',
                }}>
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    color: 'var(--text-muted)',
                    fontSize: '0.85rem',
                  }}>
                    <MapPin size={14} />
                    {project.location}
                  </span>
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    color: 'var(--text-muted)',
                    fontSize: '0.85rem',
                  }}>
                    <Calendar size={14} />
                    {project.year}
                  </span>
                </div>

                <h3 style={{
                  fontSize: '1.3rem',
                  fontWeight: 600,
                  marginBottom: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                  {project.title}
                  <ArrowUpRight size={20} style={{ color: 'var(--primary-400)', opacity: 0.6 }} />
                </h3>

                <p style={{
                  color: 'var(--text-muted)',
                  fontSize: '0.9rem',
                  lineHeight: 1.6,
                  marginBottom: '20px',
                }}>
                  {project.description}
                </p>

                {/* Stats */}
                <div style={{
                  display: 'flex',
                  gap: '24px',
                  paddingTop: '20px',
                  borderTop: '1px solid var(--border)',
                }}>
                  <div>
                    <div style={{
                      fontSize: '1.3rem',
                      fontWeight: 700,
                      color: 'var(--primary-400)',
                    }}>
                      {project.credits}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Créditos CO₂</div>
                  </div>
                  <div>
                    <div style={{
                      fontSize: '1.3rem',
                      fontWeight: 700,
                      color: 'var(--accent-400)',
                    }}>
                      {project.species}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Especies</div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <style>{`
        .projects-grid > div:hover .project-img {
          transform: scale(1.08);
        }
        @media (max-width: 768px) {
          .projects-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
};

export default Projects;
